// Shared by the browser and the existing static SEO build. No database access.
const site = require('../seo/site.json');
const { getProductLicensing } = require('../licensing/productLicensing');
const DECIMALS = { SOL: 9, USDC: 6, BONK: 5, SKR: 6 };
const native = (p) => ['native', 'free'].includes(p?.listingType);
const external = (p) => ['legacyExternal', 'externalMarketplace', 'linkOnly'].includes(p?.listingType);
const confirmedFree = (p) => native(p) && p?.isFree === true;
const hasUnverifiedUnits = (p) => /48000\s*khz/i.test(p?.description || '');
function publicDescription(p) {
  // Withhold the invalid unit claim until the original asset can be checked.
  return String(p?.description || '').replace(/48000\s*khz/gi, '').replace(/\n{3,}/g, '\n\n').trim();
}
function safePublicUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, site.origin);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password ||
      /\/(downloads|private)\//i.test(decodeURIComponent(url.pathname)) ||
      /[?&](token|signature|x-goog-[^=]*|googleaccessid)=/i.test(url.search)) return null;
    return url.href;
  } catch { return null; }
}
function previewPath(p) {
  const path = String(p?.previewFilePath || '').trim();
  if (!path || path === p?.downloadFilePath) return '';
  const object = /^products\/[^/]+\/previews\/[^/]+$/;
  if (object.test(path)) return path;
  if (/^gs:\/\//.test(path)) {
    return object.test(path.replace(/^gs:\/\/[^/]+\//, '')) ? path : '';
  }
  try {
    const url = new URL(path);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password) return '';
    const decoded = decodeURIComponent(url.pathname);
    if (/\/(downloads|private)\//i.test(decoded) ||
      /[?&](signature|x-goog-[^=]*|googleaccessid)=/i.test(url.search)) return '';
    if (url.hostname === 'firebasestorage.googleapis.com') {
      return object.test(decoded.split('/o/')[1] || '') ? path : '';
    }
    if (url.hostname === 'storage.googleapis.com') {
      return object.test(decoded.replace(/^\/[^/]+\//, '')) ? path : '';
    }
    return path; // Explicit public preview field; never fall back to the purchased file.
  } catch { return ''; }
}
function solanaPrices(p) {
  if (!native(p) || confirmedFree(p) || p?.solanaConfig?.enabled !== true) return [];
  const seen = new Set();
  return (Array.isArray(p.solanaConfig.acceptedAssets) ? p.solanaConfig.acceptedAssets : []).flatMap((asset) => {
    const currency = String(asset?.symbol || '').toUpperCase();
    if (seen.has(currency)) return [];
    seen.add(currency);
    const digits = DECIMALS[currency];
    const value = Number(asset?.amount);
    const atomic = Math.round(value * 10 ** digits); // Same calculation as checkout.
    if (digits === undefined || !Number.isFinite(value) || value <= 0 ||
      !Number.isSafeInteger(atomic) || atomic <= 0) return [];
    const amount = (atomic / 10 ** digits).toFixed(digits).replace(/\.?0+$/, '');
    return [{ method: 'solana', currency, amount, amountAtomic: String(atomic) }];
  });
}
function stripePrice(price) {
  const currency = String(price?.currency || '').toUpperCase();
  if (price?.unitAmount == null || !Number.isSafeInteger(price.unitAmount) ||
    price.unitAmount <= 0 || price.active !== true || price.type !== 'one_time' || !/^[A-Z]{3}$/.test(currency)) return null;
  try {
    const currencies = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('currency') :
      ['CAD', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'NZD', 'CHF'];
    if (!currencies.includes(currency)) return null;
    const digits = ['ISK', 'UGX'].includes(currency) ? 2 :
      new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    return { method: 'card', currency, amount: (price.unitAmount / 10 ** digits).toFixed(digits) };
  } catch { return null; }
}
function formatCardPrice(input) {
  const price = stripePrice(input);
  if (!price) return '';
  const digits = (price.amount.split('.')[1] || '').length;
  return price.currency + ' ' + new Intl.NumberFormat('en-CA', { style: 'currency',
    currency: price.currency, currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(price.amount));
}
function priceState(p) {
  if (confirmedFree(p)) return 'free';
  if (external(p)) return 'external';
  if (native(p) && p?.isFree === false && (solanaPrices(p).length ||
    (p.stripeConfig?.enabled === true && p.stripeConfig?.priceId))) return 'paid';
  return 'unknown';
}
function projectPublicProduct(p, cardPrice = null) {
  if (p?.isActive !== true || !/^[a-z0-9][a-z0-9._-]*$/i.test(p?.slug || '') ||
    typeof p?.id !== 'string' || !p.id || !p.title) return null;
  const productUrl = site.origin + '/product/' + encodeURIComponent(p.slug);
  return {
    id: p.id, name: String(p.title), description: publicDescription(p), productUrl,
    pricing: { status: priceState(p), prices: [...solanaPrices(p),
      ...(p.listingType === 'native' && !confirmedFree(p) && p.stripeConfig?.enabled && stripePrice(cardPrice) ? [stripePrice(cardPrice)] : [])] },
    previewUrl: previewPath(p) ? productUrl + '#preview' : null,
    licenseUrl: getProductLicensing(p).url,
    licensing: getProductLicensing(p),
    tags: (Array.isArray(p.tags) ? p.tags : []).filter((tag) => typeof tag === 'string').slice(0, 50),
    includedFileTypes: (Array.isArray(p.includedFileTypes) ? p.includedFileTypes : []).filter((type) => typeof type === 'string').slice(0, 50),
    availability: 'published',
    fulfillment: external(p) ? 'external-store' : native(p) ? 'account-download' : 'unavailable',
  };
}
module.exports = { native, external, confirmedFree, hasUnverifiedUnits, publicDescription,
  safePublicUrl, previewPath, solanaPrices, stripePrice, formatCardPrice, priceState, projectPublicProduct };
